import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ClearPartnerTypesAction } from '@app/modules/@core/store/partner-types/partner-types.actions';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { AuthenticationService } from '@core/services/authentication.service';
import { Logger } from '@core/services/logger.service';
import { VersionService } from '@core/services/version.service';
import {
  ClearKitCollectorTypesAction,
  ClearShippingProfilesAction,
} from '@core/store/kits/kits.actions';
import { ClearPartnersStateAction } from '@core/store/partners/partners.actions';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { ToastContainerComponent } from '@shared/components/toast-container/toast-container.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from './../environments/environment';
import { ClearRunsStateAction } from './modules/@core/store/runs/runs.actions';
import { PdfPreviewTemplateComponent } from './modules/pdf-request-form/components/pdf-preview-template/pdf-preview-template.component';
import { PdfPreviewDirective } from './modules/pdf-request-form/directives/pdf-preview.directive';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    RouterOutlet,
    PdfPreviewDirective,
    PdfPreviewTemplateComponent,
    ToastContainerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthenticationService);
  private router = inject(Router);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private coreUtilsService = inject(CoreUtilsService);
  private accountUtilsService = inject(AccountUtilsService);
  private renderer = inject(Renderer2);

  public extraClasses = [];
  favIcon: HTMLLinkElement = document.querySelector('#favIcon');
  private readonly logger = new Logger('AppComponent');

  private readonly toastService = inject(ToastService);
  protected santaOn = false;
  constructor() {
    inject(VersionService);

    if (environment.stagingFavIcon) {
      this.favIcon.href = '/assets/favicon-stg.png';
    }

    if (Math.random() < 0.05 && new Date().getMonth() === 11) {
      setTimeout(() => {
        this.santaOn = true;
      }, 10000);
    }

    combineLatest([
      this.router.events,
      toObservable(this.coreUtilsService.tabletMode),
    ]).subscribe(([event, tabletMode]) => {
      if (event instanceof NavigationEnd) {
        this.extraClasses = [];

        if (tabletMode) {
          this.extraClasses.push('tabletMode');
          this.renderer.setStyle(document.documentElement, 'font-size', '18px');
        } else {
          this.renderer.setStyle(document.documentElement, 'font-size', '16px');
        }
      }
    });
  }

  mergeRouteData(route) {
    let data: any = {};
    while (route) {
      data = { ...data, ...route.data };
      route = route.firstChild;
    }
    this.logger.info(data);
    return data;
  }

  ngOnInit() {
    if (environment.production) {
      Logger.enableProductionMode();
    }
    this.authService.initialize();
    this.logger.debug('init');
    this.setTranslations();
    this.authService
      .getAccessToken$()
      .pipe(distinctUntilChanged())
      .subscribe((token) => {
        this.logger.debug(`auth changed ${token !== null}`);
        if (token) {
          this.accountUtilsService.loadPermissions();
          const userId = this.authService.getUserId();
          Sentry.setUser(userId ? { id: userId } : null);
        } else {
          this.accountUtilsService.clearPermissions();
          Sentry.setUser(null);
        }
        if (!token) {
          this.store.dispatch([
            new ClearPartnersStateAction(),
            new ClearPartnerTypesAction(),
            new ClearRunsStateAction(),
            new ClearKitCollectorTypesAction(),
            new ClearShippingProfilesAction(),
          ]);
        }
        if (token && this.router.url.startsWith('/auth')) {
          return this.router.navigate(['/']);
        }
        if (!token && !this.router.url.startsWith('/auth')) {
          return this.router.navigate(['/auth']);
        }
      });
  }

  ngOnDestroy(): void {
    this.toastService.clear();
  }

  private setTranslations() {
    this.logger.debug('setTranslations');
    this.translate.setDefaultLang('en');
    this.translate.use('en');
  }
}
